import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAdminHotkey } from '../hooks/useAdminHotkey';
import AdminLoginModal from './AdminLoginModal';
import AdminDashboard from './AdminDashboard';

// Ties together the hidden admin hotkey (Ctrl/Cmd+A+D), the passcode modal, and
// the analytics dashboard. Token lives ONLY in component state — never in
// localStorage — so a page refresh re-locks the dashboard.
//
// 30-minute inactivity auto-lock: any user interaction (mousemove/keydown)
// bumps a timer; if it fires without a bump, we clear the token.
const IDLE_MS = 30 * 60 * 1000;

const AdminOverlay = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [token, setToken] = useState(null);
  const timerRef = useRef(null);

  const lock = useCallback(() => {
    setToken(null);
    setModalOpen(false);
  }, []);

  const bump = useCallback(() => {
    if (!token) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(lock, IDLE_MS);
  }, [token, lock]);

  useEffect(() => {
    if (!token) return;
    bump();
    const onActivity = () => bump();
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('click', onActivity);
    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('click', onActivity);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [token, bump]);

  const trigger = useCallback(() => {
    // Hotkey does nothing if already unlocked (dashboard already visible).
    if (token) return;
    setModalOpen(true);
  }, [token]);

  useAdminHotkey(trigger);

  const onAuthed = (jwt /*, ttl */) => {
    setToken(jwt);
    setModalOpen(false);
  };

  return (
    <>
      <AdminLoginModal open={modalOpen} onClose={() => setModalOpen(false)} onAuthed={onAuthed} />
      {token && <AdminDashboard token={token} onLock={lock} />}
    </>
  );
};

export default AdminOverlay;
